import * as angular from "angular";
import * as moment from "moment";
import {StaffTestImportStatus} from "../models/staff-test-import-status.model";
import {TestImportMatchStatus} from "../models/test-import-match-status.model";
import {Column, SortDirections} from "../models/column.model";

const STAFF_TEST_IMPORT_STATUS = "staffTestImportStatus";
const STUDENT_TEST_IMPORT_STATUS = "testImportProgress";

export class TestImportDrawerController {
    staffTestImportStatus: StaffTestImportStatus;
    columns: Column[];
    rows;
    districtRow;
    testImportResultFor: string;
    studentTestImportStatus: TestImportMatchStatus;

    /* @ngInject */
    constructor(
        public $route: angular.route.IRouteService,
        private $rootScope: angular.IRootScopeService
    ) { }

    $onInit() {
        this.$rootScope.$on("test-import-drawer:open", this.openDrawer);
        this.initTestImportStatusDate();
        this.columns = this.initColumns();
        this.rows = this.initRows();
    }

    private initTestImportStatusDate(): void {
        if (this.testImportResultFor === "student") {
            this.studentTestImportStatus = this.$route.current.locals[STUDENT_TEST_IMPORT_STATUS];
        } else {
            this.staffTestImportStatus = this.$route.current.locals[STAFF_TEST_IMPORT_STATUS];
        }
    }

    private initColumns() {
        if (this.testImportResultFor === "student") {
            return [
                { headerName: "School", width: 400, field: "schoolName", initialSort: ("ASC" as SortDirections) },
                { headerName: "Inactive", field: "totalStudentsDeactivated", cellRenderer: this.renderDeactivatedStudentsHtml },
                { headerName: "Created", field: "totalStudentsCreated", cellRenderer: this.renderCreatedStudentsHtml  },
                { headerName: "Matched", field: "totalStudentsMatched", cellRenderer: this.renderMatchedStudentsHtml },
                { headerName: "Transferred", field: "totalStudentsTransferred", cellRenderer: this.renderTransferStudentsHtml }
            ];
        } else {
            return [
                { headerName: "School", width: 400, field: "schoolName", initialSort: ("ASC" as SortDirections), cellRenderer: this.renderSchoolNameHtml },
                { headerName: "Inactive", field: "totalStaffDeactivated", cellRenderer: this.renderDeactivatedStaffHtml },
                { headerName: "Created", field: "totalStaffCreated", cellRenderer: this.renderCreatedStaffHtml  },
                { headerName: "Matched", field: "totalStaffMatched", cellRenderer: this.renderMatchedStaffHtml}
            ];
        }
    }

    private initRows() {
        if (this.testImportResultFor === "student") {
            return this.studentTestImportStatus.studentMatchStatuses;
        }

        return this.staffTestImportStatus.staffMatchStatuses;
    }

    /* istanbul ignore next */
    private renderSchoolNameHtml = (params) => {
        params.$scope.vm = this;
        if(params.data.schoolName) {
            if(params.data.meta) {
                return `<div> {{ data.schoolName }} </div>`;
            }
            return `{{ data.schoolName }}`;
        } else if(params.data.districtName) {
            if(params.data.meta) {
                return `<div> {{ data.districtName }} </div>`;
            }
            return `{{ data.districtName }}`;
        }
    }

    /* istanbul ignore next */
    private renderMatchedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffMatched }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    school="data.schoolId"
                    status="matched"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffMatched }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    status="matched"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffMatched}`;
        }
    }

    /* istanbul ignore next */
    private renderCreatedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffCreated }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    school="data.schoolId"
                    status="created"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffCreated }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    status="created"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffCreated}`;
        }
    }

    /* istanbul ignore next */
    private renderDeactivatedStaffHtml = (params) => {
        params.$scope.vm = this;
        if (params.data.schoolName) {
            return `
                {{ data.totalStaffDeactivated }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    school="data.schoolId"
                    status="deactivated"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else if (params.data.districtName) {
            return `
                {{ data.totalStaffDeactivated }}
                <download-csv
                    district="vm.staffTestImportStatus.districtId"
                    status="deactivated"
                    import-type="staff"
                    link-text="CSV"
                ></download-csv>
            `;
        } else {
            return `${params.data.totalStaffDeactivated}`;
        }
    }

    /* istanbul ignore next */
    private renderMatchedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsMatched }}
            <download-csv
                district="vm.studentTestImportStatus.districtId"
                school="data.schoolId"
                status="matched"
                import-type="student"
                link-text="CSV"
            ></download-csv>
        `;
    }

    /* istanbul ignore next */
    private renderDeactivatedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsDeactivated }}
            <download-csv
                district="vm.studentTestImportStatus.districtId"
                school="data.schoolId"
                status="deactivated"
                import-type="student"
                link-text="CSV"
            ></download-csv>
        `;
    }

    /* istanbul ignore next */
    private renderCreatedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsCreated }}
            <download-csv
                district="vm.studentTestImportStatus.districtId"
                school="data.schoolId"
                status="created"
                import-type="student"
                link-text="CSV"
            ></download-csv>
        `;
    }

    private renderTransferStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsTransferred }}
            <download-csv
                district="vm.studentTestImportStatus.districtId"
                school="data.schoolId"
                status="transferred"
                import-type="student"
                link-text="CSV"
            ></download-csv>
        `;
    }

    openDrawer = (_event) => {
        this.$rootScope.$emit("drawer:open", "test-import-drawer");
    }

    closeDrawer() {
        this.$rootScope.$emit("drawer:close", "test-import-drawer");
        this.$rootScope.$emit("test-import-drawer:close");
    }

    getTestImportCompletedDate(): string {
        let date = null;

        if (this.testImportResultFor === "student") {
            date = this.studentTestImportStatus.completedOn;
        } else {
            date = this.staffTestImportStatus.completedOn;
        }

        const momentLocal = (moment as any).tz(date, "UTC").local();
        return `${momentLocal.format("MMM DD YYYY")} at ${momentLocal.format("hh:mm A")}`;
    }

    getTotalSchoolsCompleted(): number {
        if (this.testImportResultFor === "student") {
            return  this.studentTestImportStatus.studentMatchStatuses.length;
        }

        return this.staffTestImportStatus.staffMatchStatuses.length;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/test-import-drawer/test-import-drawer.controller.ts